import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const values = [
  { icon: "ri-compass-3-line", title: "新鲜快讯", text: "以快为要，今日新鲜快讯与动态第一时间跟进，让读者抢先掌握一手资讯。" },
  { icon: "ri-focus-3-line", title: "客观真实", text: "坚持事实核实，谨慎表述，报道来源清晰，还原事件本来面貌。" },
  { icon: "ri-heart-3-line", title: "快乐吃瓜", text: "用轻松又不失严谨的方式呈现热门事件，让追更变成一件快乐的小事。" },
  { icon: "ri-community-line", title: "健康向上", text: "拒绝低俗与博眼球，始终倡导健康、理性、积极的网络讨论氛围。" },
];

const principles = [
  "素材来源尽量明确，无法核实的部分如实标注，不随意猜测定性。",
  "涉及当事人的报道保持审慎与尊重，不放大矛盾、不消费他人隐私。",
  "标题避免夸张与误导，摘要如实反映正文，杜绝“标题党”。",
  "内容坚持原创与整理并重，引用处注明来源，尊重创作者权益。",
  "欢迎读者与我们交流线索与建议，一经核实将及时跟进与更正。",
];

const voices = [
  { name: "追更小分队", role: "普通读者", text: "每天刷新成习惯，热点进展跟得又快又全，界面也很清爽，追更体验很舒服。" },
  { name: "影迷阿凯", role: "影视爱好者", text: "资讯更新及时，专题策划有深度，能一次把一部剧或一场颁奖看懂，很省心。" },
  { name: "上班族Momo", role: "普通读者", text: "通勤路上刷一刷就掌握当天新鲜事，订阅推送也贴心，不会刷屏打扰。" },
];

const stats = [
  { num: "1,200+", label: "每日快讯条数" },
  { num: "6 大", label: "常设内容栏目" },
  { num: "10 亿+", label: "累计话题阅读" },
  { num: "24h", label: "全天实时跟进" },
];

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `关于 ${siteInfo.brandFull}`,
  url: "https://bpj87u.cn/about",
  mainEntity: {
    "@type": "Organization",
    name: siteInfo.brandFull,
    description: siteInfo.desc,
    email: siteInfo.email,
  },
};

export default function About() {
  useSeo({
    title: "关于我们 - 品牌介绍与内容理念",
    description: `了解${siteInfo.brandFull}的网站定位、发展理念与内容原则。我们专注新鲜快讯与热门事件实时更新，坚持客观真实、健康向上，为你带来有温度的吃瓜追更体验。`,
    keywords: "关于海角爆,海角爆品牌介绍,海角爆内容原则,海角爆发展理念,娱乐资讯门户",
    path: "/about",
    type: "website",
    jsonLd: aboutJsonLd,
  });

  return (
    <>
      <Navbar />
      <main>
        <div className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>
          </div>
        </div>

        {/* 首屏品牌介绍 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-sm font-medium text-accent-900">
                <i className="ri-heart-line"></i> About {siteInfo.brand}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                关于 <span className="text-primary-500">海角爆</span> ，
                <br />
                让每一件新鲜事被看见
              </h1>
              <p className="mt-5 max-w-xl text-[15px] leading-7 text-foreground-700">
                {siteInfo.brandFull} 创立于 {siteInfo.since} 年，是一个专注新鲜快讯与热门事件实时更新的娱乐资讯门户。我们以“最新、实时、抢先看”为使命，聚合娱乐、影视、综艺、科技圈与生活方式等领域的热门动态，帮助忙碌的你花更少的时间，掌握更多值得讨论的新鲜事。
              </p>
              <p className="mt-3 max-w-xl text-[15px] leading-7 text-foreground-700">
                在这里，今日快讯与实时动态随时刷新，热门事件进展第一时间同步。我们不追求制造焦虑，只希望用靠谱、及时又有趣的内容，陪你吃瓜追更、不错过任何精彩节点。
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/#subscribe" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
                  <i className="ri-notification-3-line"></i> 订阅新鲜快讯
                </Link>
                <a href={`mailto:${siteInfo.email}`} className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-6 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200">
                  <i className="ri-mail-line"></i> 联系我们
                </a>
              </div>
            </div>
            <div className="overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20modern%20newsroom%20workspace%20with%20warm%20desk%20lamps%20laptops%20and%20coffee%20cups%20evening%20warm%20amber%20editorial%20photography&width=1000&height=760&seq=cap-about-01&orientation=landscape"
                alt="海角爆编辑部工作台"
                className="h-72 w-full object-cover md:h-[420px]"
                loading="lazy"
              />
            </div>
          </div>

          {/* 数据 */}
          <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-background-100 p-5 text-center">
                <p className="font-heading text-2xl font-black text-primary-600">{s.num}</p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 定位与理念 */}
        <section className="bg-background-100/70 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
                  <i className="ri-flag-2-line text-primary-500"></i> 网站定位
                </h2>
                <p className="mt-4 text-[15px] leading-7 text-foreground-700">
                  我们定位于“轻量、及时、有趣”的泛资讯平台。相比长篇解读，海角爆更擅长把庞杂的信息快速提炼成容易理解的快讯与专题，让你在碎片时间里也能抓住重点、跟上热点。
                </p>
                <p className="mt-3 text-[15px] leading-7 text-foreground-700">
                  从红毯造型到综艺名场面，从影视热映到数码新品，我们记录的是当下大家正在关注与讨论的事，努力成为你每天顺手打开、看完会说一句“懂了”的地方。
                </p>
              </div>
              <div>
                <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
                  <i className="ri-route-line text-accent-600"></i> 发展理念
                </h2>
                <p className="mt-4 text-[15px] leading-7 text-foreground-700">
                  我们相信：好内容的价值在于“被需要”。因此海角爆坚持以读者需求为中心不断打磨产品——信息更新更快一点、分类更好找一点、阅读更轻松一点。
                </p>
                <p className="mt-3 text-[15px] leading-7 text-foreground-700">
                  未来，我们希望进一步打通多元的内容形态，让实时跟进、深度回顾与社区互动更好地结合，把“吃瓜追更”这件事，做得越来越贴心。
                </p>
              </div>
            </div>

            {/* 核心价值 */}
            <div className="mt-12">
              <h2 className="text-center font-heading text-2xl font-black text-foreground-950">我们坚持的 4 件事</h2>
              <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {values.map((v) => (
                  <div key={v.title} className="rounded-2xl bg-background-50 p-6 hover:-translate-y-0.5 transition-transform">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                      <i className={`${v.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
                <i className="ri-shield-check-line text-accent-600"></i> 内容原则
              </h2>
              <p className="mt-4 text-[15px] leading-7 text-foreground-700">
                无论快讯还是专题，我们都希望在“快”与“准”之间找到平衡。以下是编辑部内容创作的基本准则。
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-4 lg:col-span-3">
              {principles.map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl bg-background-100 p-4">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">{i + 1}</span>
                  <p className="text-sm leading-relaxed text-foreground-800">{p}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="bg-background-100/70 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="text-center font-heading text-2xl font-black text-foreground-950">听听读者怎么说</h2>
            <p className="mx-auto mt-2 max-w-lg text-center text-sm text-foreground-500">来自真实用户的追更体验分享，是我们持续更新的最大动力。</p>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((v) => (
                <figure key={v.name} className="rounded-2xl bg-background-50 p-6">
                  <div className="flex items-center gap-1 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-foreground-800">“{v.text}”</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-heading font-bold text-secondary-700">{v.name.slice(0, 1)}</span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{v.name}</p>
                      <p className="text-xs text-foreground-500">{v.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系方式 CTA */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <div className="rounded-3xl bg-background-100 p-8 md:p-12">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <h2 className="font-heading text-2xl font-black text-foreground-950">想投稿、纠错或交流合作？</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  欢迎通过邮件与我们联系，也欢迎回到首页使用“我要爆料”提交新鲜线索，我们会尽快回复。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a href={`mailto:${siteInfo.email}`} className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
                  <i className="ri-mail-send-line"></i> {siteInfo.email}
                </a>
                <Link to="/" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-200">
                  <i className="ri-home-line"></i> 返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}